
import { Dispatch, SetStateAction } from 'react';
import CustomSelect from '../../CommonComponents/CustomSelect';
import { checkNumberInputValidation, getSelectedValue } from '../utils';
import { Form } from 'react-bootstrap';
import { containsSpecialChars, getCharacterLimitMessage, getSpecialCharacterMessage, onKeyDown, showToastMessage, validateNumberWithoutDecimal } from '../../../utils/commonFunctions';
import _ from 'lodash';
import { BUS_COMPONENTS_TYPES, DEFAULT_POWER_SINK_DATA, DEFAULT_POWER_SINK_FIELDS } from '../utils/constants';

interface BusCompProps {
    formData: any;
    setFormData: Dispatch<SetStateAction<any>>;
    onChange: (name: string, value: string) => void;
}

const BusCompForm = ({ formData, setFormData, onChange }: BusCompProps) => {
    const max_length = 26;
    const default_component_data = {
        'friendly-name': '',
        'numUnits': '',
    }

    const disabledAddComponentButton = () => {
        const components = formData?.component_specific_attributes?.Components;
        if (!components || components.length === 0) return false;
        return components.some((item: any) =>
            _.some(item, (value, key) => _.isEmpty(key) || _.isEmpty(value))
        );
    };


    const handleNamechange = (componentIndex: number, value: string) => {
        if (containsSpecialChars(value)) {
            const message = getSpecialCharacterMessage()
            showToastMessage('info', message)
        } else if (value.length > max_length) {
            const message = getCharacterLimitMessage(max_length)
            showToastMessage('info', message)
        } else {
            const data = formData?.['component_specific_attributes']?.['Components'][componentIndex];
            data['friendly-name'] = value;
            setFormData((prev) => ({
                ...prev,
                component_specific_attributes: {
                    ...prev?.component_specific_attributes,
                    Components: [
                        ...prev?.component_specific_attributes?.['Components'].slice(0, componentIndex),
                        data,
                        ...prev?.component_specific_attributes?.['Components'].slice(componentIndex + 1)
                    ]
                }
            }))
        }
    }

    const handleChangeProperty = (componentIndex: number, propertyIndex: number, property: string, value: string) => {
        if (value?.length <= 26) {
            setFormData(prev => {
                const components = [...(prev?.component_specific_attributes?.Components || [])];
                const targetComponent = { ...components[componentIndex] };
                const entries = Object.entries(targetComponent);
                if (propertyIndex >= entries.length) return prev;
                const updatedEntries = entries.map(([k, v], index) => {
                    if (index !== propertyIndex) return [k, v];
                    if (property === 'key') return [value, v]; // update key
                    return [k, value]; // update value
                });
                const updatedComponent = Object.fromEntries(updatedEntries);
                components[componentIndex] = updatedComponent;
                return {
                    ...prev,
                    component_specific_attributes: {
                        ...prev.component_specific_attributes,
                        Components: components
                    }
                };
            });

        } else {
            showToastMessage('info', getCharacterLimitMessage(max_length))
        }
    }

    const handleProperty = (mode: string, componentIndex: number, key?: string) => {
        const data = formData?.['component_specific_attributes']?.['Components'][componentIndex];
        mode === 'add' ? data[''] = '' : delete data[key || ''];
        setFormData((prev) => ({
            ...prev,
            component_specific_attributes: {
                ...prev?.component_specific_attributes,
                Components: [
                    ...prev?.component_specific_attributes?.['Components'].slice(0, componentIndex),
                    data,
                    ...prev?.component_specific_attributes?.['Components'].slice(componentIndex + 1)
                ]
            }
        }))
    }

    const handleDeleteComponent = (componentIndex: number) => {
        setFormData((prev) => ({
            ...prev,
            component_specific_attributes: {
                ...prev?.component_specific_attributes,
                Components: [
                    ...prev?.component_specific_attributes?.['Components'].slice(0, componentIndex),
                    ...prev?.component_specific_attributes?.['Components'].slice(componentIndex + 1)
                ]
            }
        }))
    }

    const handleChangeCatalog = (selected: any) => {
        if (selected?.value !== 'Payload SSD') {
            delete formData?.['size_in_MB']
        }
        const data = {
            catalogueType: selected?.value || '',
            component_specific_attributes: selected?.value === 'iADCS' ? {
                Components: []
            } : {}
        }
        setFormData((prev) => ({
            ...prev,
            ...data
        }))
    }

    const handleChangeMemory = (event: any) => {
        const value = event.target.value
        if (!isNaN(value) && value.length <= 5) {
            setFormData((prev) => ({
                ...prev,
                size_in_MB: event.target.value
            }))
        } else if (value.length > 5) {
            showToastMessage("info", "Maximum 5 digits allowed");
        }
    }

    const handlePowerSink = (power_sink: any, componentIndex) => {
        if (!power_sink) {
            setFormData((prev) => ({
                ...prev,
                component_specific_attributes: {
                    ...prev?.component_specific_attributes,
                    Components: [
                        ...prev?.component_specific_attributes?.['Components'].slice(0, componentIndex),
                        {
                            ...prev?.component_specific_attributes?.['Components'][componentIndex],
                            power_sink: DEFAULT_POWER_SINK_DATA
                        },
                        ...prev?.component_specific_attributes?.['Components'].slice(componentIndex + 1)
                    ]
                }
            }))
        } else {
            const data = formData?.['component_specific_attributes']?.['Components'][componentIndex];
            delete data['power_sink'];
            setFormData((prev) => ({
                ...prev,
                component_specific_attributes: {
                    ...prev?.component_specific_attributes,
                    Components: [
                        ...prev?.component_specific_attributes?.['Components'].slice(0, componentIndex),
                        data,
                        ...prev?.component_specific_attributes?.['Components'].slice(componentIndex + 1)
                    ]
                }
            }))
        }
    }

    const onChangePowerSink = (componentIndex: number, power_sink: any, key: string, value: string) => {
        setFormData((prev) => ({
            ...prev,
            component_specific_attributes: {
                ...prev?.component_specific_attributes,
                Components: [
                    ...prev?.component_specific_attributes?.['Components'].slice(0, componentIndex),
                    {
                        ...prev?.component_specific_attributes?.['Components'][componentIndex],
                        power_sink: {
                            ...power_sink,
                            [key]: value
                        },
                    },
                    ...prev?.component_specific_attributes?.['Components'].slice(componentIndex + 1)
                ]
            }
        }))
    }

    const onChangeNumUnits = (value: string, componentIndex: number, max_length: number) => {
        if (!checkNumberInputValidation(value, max_length)) return;
        const data = formData?.['component_specific_attributes']?.['Components'][componentIndex];
        data['numUnits'] = value;
        setFormData((prev) => ({
            ...prev,
            component_specific_attributes: {
                ...prev?.component_specific_attributes,
                Components: [
                    ...prev?.component_specific_attributes?.['Components'].slice(0, componentIndex),
                    data,
                    ...prev?.component_specific_attributes?.['Components'].slice(componentIndex + 1)
                ]
            }
        }))
    }

    return (
        <div className='w-100 d-flex flex-column gap-3'>
            <div className="w-100 d-flex flex-row gap-2">
                <div className="flex-1 d-flex flex-column">
                    <div className="cp-text">Catalog Type</div>
                    <CustomSelect
                        options={BUS_COMPONENTS_TYPES}
                        placeholder={"Select Catalog Type"}
                        onChange={handleChangeCatalog}
                        value={getSelectedValue(formData?.catalogueType)}
                        aria-label="catalog type"

                    />
                </div>
                {formData?.['catalogueType'] === 'Payload SSD' &&
                    <div className="w-50 d-flex flex-column">
                        <span className="cp-text">Memory (GB)</span>
                        <Form.Control
                            type="text"
                            required
                            placeholder="Component name"
                            value={formData?.['size_in_MB']}
                            onKeyDown={validateNumberWithoutDecimal}
                            onChange={handleChangeMemory}
                        />
                    </div>
                }
            </div>
            {(formData?.['catalogueType'] === 'iADCS') &&
                <div className='w-100 d-flex flex-column'>
                    <span className='cp-text fs-14'>Custom Component </span>
                    {formData?.['component_specific_attributes']?.['Components']?.length > 0 &&
                        formData?.['component_specific_attributes']?.['Components']?.map((item: any, componentIndex: number) => {
                            const componentsLength = formData?.['component_specific_attributes']?.['Components']?.length
                            const label = `Component ${componentIndex < 9 ? `0${componentIndex + 1}` : componentIndex + 1}`
                            const disabledAddPropertyButton = Object.keys(item).find(key => _.isEmpty(key.trim()) && _.isEmpty(item[key].trim())) ? true : false
                            const power_sink = item?.['power_sink']
                            return (
                                <div className='w-100 d-flex flex-column gap-2' key={componentIndex}>
                                    <div className='w-100 d-flex align-items-center justify-content-between'>
                                        <span className='cp-text'>{label}</span>
                                        {(componentsLength >= 1 && componentIndex <= 5) &&
                                            <button
                                                className="h-100 p-2 w-auto bg-transparent border d-flex justify-content-center align-items-center"
                                                onClick={() => handleDeleteComponent(componentIndex)}
                                            >
                                                <i className="fe fe-x fs-16"></i>
                                            </button>
                                        }
                                    </div>
                                    <div className="w-100 d-flex flex-row gap-2">
                                        <div className="w-50 d-flex flex-column">
                                            <span className="cp-text">Name</span>
                                            <Form.Control
                                                type="text"
                                                required
                                                placeholder="Component name"
                                                value={item?.['friendly-name']}
                                                onChange={(event: any) => { handleNamechange(componentIndex, event.target.value) }}
                                            />
                                        </div>
                                        <div className="w-50 d-flex flex-column">
                                            <span className="cp-text">Number of units</span>
                                            <Form.Control
                                                type="text"
                                                required
                                                placeholder='Number of units'
                                                value={item?.['numUnits']}
                                                onKeyDown={onKeyDown}
                                                onChange={(event: any) => {
                                                    onChangeNumUnits(event.target.value, componentIndex, 5)
                                                }}
                                            />
                                        </div>
                                    </div>
                                    <div className="d-flex flex-column">
                                        <div className="d-flex align-items-center justify-content-between">
                                            <div className="cp-text fs-14">Power Sink</div>
                                            <button
                                                className="h-100 p-2 w-auto bg-transparent border d-flex justify-content-center align-items-center"
                                                onClick={() => {
                                                    handlePowerSink(power_sink, componentIndex)
                                                }}
                                            >
                                                {!power_sink ? <i className="fe fe-plus fs-16"></i> : <i className="fe fe-x fs-16"></i>}
                                            </button>
                                        </div>
                                        <div className="d-flex align-items-start gap-2">
                                            {power_sink && DEFAULT_POWER_SINK_FIELDS.map((field: any, index: number) => (
                                                <div key={index} className="w-25 d-flex flex-row gap-2">
                                                    <div className="w-100 d-flex flex-column">
                                                        <div className="cp-text">{field.label}</div>
                                                        <Form.Control
                                                            type="text"
                                                            required
                                                            placeholder={field.label}
                                                            value={power_sink?.[field.key]}
                                                            onKeyDown={onKeyDown}
                                                            onChange={(event: any) => {
                                                                onChangePowerSink(componentIndex, power_sink, field.key, event.target.value)
                                                            }}
                                                        >
                                                        </Form.Control>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                    <div className='component__separator mt-3' />
                                    <div className='w-100 d-flex flex-column gap-2'>
                                        <div className='w-100 d-flex align-items-center justify-content-between'>
                                            <span className='cp-text'>Property</span>
                                            {Object.keys(item)?.length <= 5 &&
                                                <button
                                                    className="h-100 p-2 w-auto bg-transparent border d-flex justify-content-center align-items-center"
                                                    disabled={disabledAddPropertyButton}
                                                    onClick={() => handleProperty('add', componentIndex)}
                                                >
                                                    <i className="fe fe-plus fs-16"></i>
                                                </button>
                                            }
                                        </div>
                                        <div className='w-100 d-flex flex-column gap-2'>
                                            {Object.entries(item).map(([key, value], propertyIndex) => {
                                                if (key !== 'friendly-name' && key !== 'numUnits' && key !== 'power_sink') {
                                                    return (
                                                        <div className='w-100 d-flex align-items-center gap-2' key={propertyIndex}>
                                                            <div className="w-50">
                                                                <Form.Control
                                                                    type="text"
                                                                    className="form-control"
                                                                    placeholder="Property Name"
                                                                    value={key}
                                                                    onChange={(e) => {
                                                                        handleChangeProperty(componentIndex, propertyIndex, 'key', e.target.value)
                                                                    }}
                                                                />
                                                            </div>
                                                            <div className="w-50">
                                                                <Form.Control
                                                                    type="text"
                                                                    className="form-control"
                                                                    placeholder="Value"
                                                                    value={value as string}
                                                                    onChange={(e) => handleChangeProperty(componentIndex, propertyIndex, 'value', e.target.value)}
                                                                />
                                                            </div>
                                                            <button
                                                                className="h-100 py-2 px-1 w-5 bg-transparent border d-flex justify-content-center align-items-center"
                                                                onClick={() => handleProperty('delete', componentIndex, key)}
                                                            >
                                                                <i className="fe fe-x fs-16"></i>
                                                            </button>
                                                        </div>
                                                    )
                                                }
                                            })}
                                        </div>
                                    </div>
                                    <div className='component__separator mt-3' />
                                </div>
                            )
                        })
                    }
                </div>
            }
            {formData?.['catalogueType'] === 'iADCS' &&
                <button
                    disabled={disabledAddComponentButton()}
                    className="cp-add-btn w-100 py-2 d-flex align-items-center justify-content-center mt-4"
                    onClick={() => {
                        setFormData((prev: any) => ({
                            ...prev,
                            'component_specific_attributes': {
                                ...prev?.['component_specific_attributes'],
                                'Components': prev?.['component_specific_attributes']?.['Components'] ? [...prev?.['component_specific_attributes']?.['Components'], default_component_data] : [default_component_data]
                            }
                        }))
                    }}
                >
                    <i className="fe fe-plus me-2"></i>
                </button>
            }
        </div>
    )
}

export default BusCompForm